import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  FileCode, RefreshCw, Edit, History, Beaker, Tag,
} from "lucide-react";

export default function AdminPromptsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/prompts");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando prompts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (p.prompt_key || "").toLowerCase().includes(s) ||
      (p.name || "").toLowerCase().includes(s) ||
      (p.description || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-gray-900 mb-1 flex items-center gap-2">
            <FileCode className="w-7 h-7 text-brand-burgundy" />
            Prompts del sistema
          </h1>
          <p className="text-sm text-gray-600">Edita los prompts LLM usados en todo el sistema. Cada cambio crea una nueva versión.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/prompts/ab-tests")}
            data-testid="goto-ab-tests"
          >
            <Beaker className="w-4 h-4 mr-2" /> Pruebas A/B
          </Button>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </div>

      <Input
        placeholder="Buscar por key, nombre o descripción…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 max-w-md"
        data-testid="prompts-search"
      />

      <div className="space-y-3">
        {loading && items.length === 0 && (
          <div className="p-8 text-center text-gray-500">Cargando…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500 border border-dashed border-gray-200 rounded-sm">
            No hay prompts que coincidan.
          </div>
        )}
        {filtered.map((p) => (
          <div
            key={p.id}
            className="border border-gray-200 rounded-sm bg-white p-4 hover:border-brand-burgundy/40 transition-colors"
            data-testid={`prompt-card-${p.prompt_key}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-medium text-gray-900">{p.name}</h3>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm border border-emerald-200 font-semibold">
                    v{p.version} activa
                  </span>
                  {p.model && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-sm border border-gray-200">
                      {p.model}
                    </span>
                  )}
                  <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 text-gray-600">{p.prompt_key}</code>
                </div>
                <p className="text-sm text-gray-700 mb-2">{p.description}</p>
                <div className="text-[11px] text-gray-500 flex gap-3 flex-wrap">
                  <span>{p.content?.length || 0} caracteres</span>
                  <span>·</span>
                  <span>{p.total_versions} versión{p.total_versions === 1 ? "" : "es"} total{p.total_versions === 1 ? "" : "es"}</span>
                  <span>·</span>
                  <span>Actualizado: {formatDate(p.updated_at)}</span>
                </div>
                {Array.isArray(p.variables) && p.variables.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {p.variables.map((v) => (
                      <span key={v} className="text-[10px] bg-brand-cream text-brand-burgundy px-1.5 py-0.5 rounded-sm border border-brand-burgundy/20">
                        <Tag className="inline w-2.5 h-2.5 mr-0.5" /><code>{v}</code>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin/prompts/${p.prompt_key}`)}
                  data-testid={`edit-prompt-${p.prompt_key}`}
                >
                  <Edit className="w-4 h-4 mr-1" /> Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/admin/prompts/${p.prompt_key}/history`)}
                  data-testid={`history-prompt-${p.prompt_key}`}
                >
                  <History className="w-4 h-4 mr-1" /> Historial
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
